import React, { useState, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Divider,
} from "@material-ui/core";
import {
  verifyAchDescriptor,
  verifyAchMicrodeposits,
} from "../../../services/apiservices/users";
import CloseIcon from "@mui/icons-material/Close";
import { IconButton } from "@mui/material";
import { Input } from "../../../components/ui/input";

const ACHVerification = ({
  open,
  type,
  paymentIntentId,
  onSuccess,
  onClose,
  verificationLoader,
  setVerificationLoader,
}) => {
  const [amount1, setAmount1] = useState("");
  const [amount2, setAmount2] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const [descriptorValues, setDescriptorValues] = useState([
    "",
    "",
    "",
    "",
    "",
    "",
  ]);
  const descriptorRefs = useRef([]);

  const handleDescriptorChange = (value, index) => {
    if (!/^[A-Za-z0-9]?$/.test(value)) return;

    let updated = [...descriptorValues];
    updated[index] = value.toUpperCase();
    setDescriptorValues(updated);

    if (value && index < 5) {
      descriptorRefs.current[index + 1].focus();
    }
  };

  const handleDescriptorKey = (e, index) => {
    if (e.key === "Backspace" && !descriptorValues[index] && index > 0) {
      descriptorRefs.current[index - 1].focus();
    }
  };

  const handleDescriptorCodeVerification = async () => {
    const code = descriptorValues.join("");
    if (code.length !== 6) {
      setErrorMsg("Please enter all 6 characters.");
      return;
    }

    setVerificationLoader(true);
    setErrorMsg("");

    const success = await verifyAchDescriptor(paymentIntentId, code);

    if (!success) {
      setErrorMsg("Invalid descriptor code.");
      setVerificationLoader(false);
      return;
    }

    onSuccess();
  };

  const handleMicroDepositVerification = async () => {
    setVerificationLoader(true);
    setErrorMsg("");

    const parseAmount = (value) => {
    if (!value) return 0;
      const num = parseFloat(value);

      if (num < 1) {
        return Math.round(num * 100);
      } else {
        return Math.round(num);
      }
    };

    const amountsInCents = [
      parseAmount(amount1),
      parseAmount(amount2),
    ];

    const success = await verifyAchMicrodeposits(paymentIntentId, amountsInCents);

    if (!success) {
      setErrorMsg("Verification failed. Please double-check your amounts.");
      setVerificationLoader(false);
      return;
    }

    onSuccess();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <div className="d-flex flex-center flex-space-between dark:bg-[#1A1F2C] mb-8">
        <Typography
          variant="body"
          className="mt-20 dialogTitle dark:text-white"
        >
          Bank Account Verification
        </Typography>
      </div>

      <Divider />

      <DialogContent className="pv-20 dark:bg-[#1A1F2C]">

        <div
          style={{ marginBottom: "30px", fontSize:"16px", fontWeight: 500, color: "#FF9800", lineHeight: "1.5" }}
        >
          Note: Any subscription or plan changes will be applied only after your bank account verification is complete.
        </div>

        {type === "microdeposit" && (
          <>
            <div
              className="mt-10 dark:text-white"
              style={{
                fontSize: "18px",
                fontWeight: "600",
                marginBottom: "16px",
              }}
            >
              Enter the <strong>two micro-deposit amounts</strong> Stripe sent
              to your bank account.
            </div>

            <Typography
              variant="body2"
              className="dark:text-gray-300"
              style={{ marginBottom: "22px" }}
            >
              These amounts are in <strong>cents</strong> (for example: 0.32 → enter <strong>32</strong>).
            </Typography>

            {/* Amount 1 */}
            <div style={{ marginBottom: "20px" }}>
              <Typography
                className="dark:text-white"
                style={{ marginBottom: "6px", fontWeight: 500 }}
              >
                Amount 1
              </Typography>

              <div
                className="d-flex"
                style={{ alignItems: "center", gap: "8px" }}
              >
                <div
                  style={{
                    padding: "10px 14px",
                    border: "1px solid #ccc",
                    borderRadius: 6,
                    background: "#f5f5f5",
                    fontWeight: 600,
                    marginRight: "8px",
                  }}
                >
                  $
                </div>

                <Input
                  fullWidth
                  placeholder="Enter amount in cents"
                  type="number"
                  step="0.01" 
                  value={amount1}
                  onChange={(e) => setAmount1(e.target.value)}
                />
              </div>
            </div>

            {/* Amount 2 */}
            <div style={{ marginBottom: "10px" }}>
              <Typography
                className="dark:text-white"
                style={{ marginBottom: "6px", fontWeight: 500 }}
              >
                Amount 2
              </Typography>

              <div
                className="d-flex"
                style={{ alignItems: "center", gap: "8px" }}
              >
                <div
                  style={{
                    padding: "10px 14px",
                    border: "1px solid #ccc",
                    borderRadius: 6,
                    background: "#f5f5f5",
                    fontWeight: 600,
                    marginRight: "8px",
                  }}
                >
                  $
                </div>

                <Input
                  fullWidth
                  placeholder="Enter amount in cents"
                  type="number"
                  step="0.01" 
                  value={amount2}
                  onChange={(e) => setAmount2(e.target.value)}
                />
              </div>
            </div>
          </>
        )}

        {type === "descriptor_code" && (
          <>
            <div
              className="mt-10 dark:text-white"
              style={{
                fontSize: "18px",
                fontWeight: "600",
                marginBottom: "8px",
              }}
            >
              Enter the 6-character <strong>descriptor code</strong> from your
              bank statement. For example: <strong>SM11AA</strong>
            </div>

            <div style={{ display: "flex", justifyContent:"center",  gap: "30px", marginTop: "35px" }}>
              {[0, 1, 2, 3, 4, 5]?.map((i) => (
                <input
                  key={i}
                  ref={(el) => (descriptorRefs.current[i] = el)}
                  maxLength={1}
                  value={descriptorValues[i]}
                  onChange={(e) => handleDescriptorChange(e.target.value, i)}
                  onKeyDown={(e) => handleDescriptorKey(e, i)}
                  style={{
                    width: "50px",
                    height: "50px",
                    fontSize: "22px",
                    textAlign: "center",
                    borderRadius: "8px",
                    border: "1px solid #d1d1d1",
                  }}
                />
              ))}
            </div>
          </>
        )}

        {errorMsg && (
          <Typography sx={{ mt: 2 }} color="error">
            {errorMsg}
          </Typography>
        )}
      </DialogContent>

      <DialogActions className="mt-20 mb-15 dark:bg-[#1A1F2C]">
        <Button
          onClick={onClose}
          variant="outlined"
          className="text-[#020817] dark:text-white dark:border-gray-500"
          style={{ borderRadius: 4 }}
          disabled={verificationLoader}
        >
          Cancel
        </Button>

        <Button
          onClick={
            type === "microdeposit"
              ? handleMicroDepositVerification
              : handleDescriptorCodeVerification
          }
          variant="contained"
          style={{ backgroundColor: "green", color: "white", borderRadius: 4 }}
          disabled={verificationLoader}
        >
          {verificationLoader ? "Verifying..." : "Verify"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ACHVerification;
